import { Snapshot } from '@com-delta-idp/mnx-skymiles-overview-ui';
import {
  AEMErrorMessage,
  checkDefined,
  checkErrors,
  getEmptyContent,
  getGenericLabel,
  getLinkWithId,
  isDisplay,
  isError,
  isValidData
} from '@com-delta-idp/mnx-skymiles-shared';
import { logError } from '@com-delta-idp/mnx-utils';
import { BorderType, DateColumn, EntryItem } from '@com-delta-omnichannel/aura';
import { AEMSnapshot, TYPE } from '../models';
import {
  AccountSnapshotResponse,
  SnapshotRecentAccountActivity
} from '../models/api/account-snapshot.interfaces';
import {
  getActivityAmounts,
  getHeader,
  validateActivity
} from '../utils/snapshot.utils';

export const getSnapshot = (
  aem: AEMSnapshot | undefined,
  snapshot: AccountSnapshotResponse | undefined,
  featureFlag: boolean | undefined,
  errors: (string | undefined)[],
  aemErrorMessages: AEMErrorMessage[] | undefined
): Snapshot => {
  try {
    if (checkErrors(errors, aemErrorMessages)) {
      return isError<Snapshot>(
        'snapshot',
        aemErrorMessages as AEMErrorMessage[],
        featureFlag
      );
    }
    return isValidData<
      AEMSnapshot,
      [
        AEMSnapshot | undefined,
        AccountSnapshotResponse | undefined,
        boolean | undefined
      ]
    >('title', aem, snapshot, featureFlag)
      ? adaptSnapshot(
          aem as AEMSnapshot,
          featureFlag as boolean,
          snapshot as AccountSnapshotResponse
        )
      : isDisplay<Snapshot>(featureFlag);
  } catch (error) {
    logError(error as Error, 'SKYMILES', 'OVERVIEW', 'SNAPSHOT');
    return getEmptyContent<Snapshot>('snapshot', aemErrorMessages, true);
  }
};

export const adaptSnapshot = (
  aem: AEMSnapshot,
  featureFlag: boolean,
  snapshot: AccountSnapshotResponse
): Snapshot => {
  if (validateActivity(snapshot) && featureFlag) {
    return {
      title: aem.title,
      toggle: getHeader(aem),
      activities: getActivities(snapshot),
      action: getLinkWithId(
        aem.link.label,
        aem.link.url,
        checkDefined(aem.link.id)
      ),
      disabled: !featureFlag,
      loaded: true
    };
  } else {
    return getEmptyContent<Snapshot>('', [], true, true);
  }
};

const getActivities = (snapshot: AccountSnapshotResponse): EntryItem[] => {
  const activity: EntryItem[] = [];
  snapshot.recentActivities.forEach((values: SnapshotRecentAccountActivity) => {
    const result = {
      content: getGenericLabel(getName(values), ''),
      state: '',
      icon: '',
      dateColumn: getActivityDate(values),
      value: getActivityAmounts(values),
      ariaLabel: {
        icon: ''
      },
      border: 'none' as BorderType
    };
    activity.push(result);
  });
  return activity;
};

const getActivityDate = (values: SnapshotRecentAccountActivity): DateColumn => {
  const ActivityDate = new Date(values.activityLocalDate + 'T12:00:00Z');
  const month = ActivityDate.toLocaleString('default', {
    month: 'short'
  }).toLocaleUpperCase();
  const date = ActivityDate.toLocaleString('default', { day: 'numeric' });
  return {
    month: `${month}`,
    date: `${date}`
  };
};

const getName = (values: SnapshotRecentAccountActivity): string => {
  const activityTypeCode = values.activityTypeCode.split('-')[0].trim();
  return activityTypeCode == TYPE.AIR_ACTIVITY_CODE
    ? validateDescription(values.activityDesc)
    : values.activityRewards[0].rewardDesc;
};

const validateDescription = (description: string): string => {
  const originDestination = description.split('-');
  if (originDestination.length > 0) {
    return `<div alt="${originDestination[0]} to ${originDestination[1]}"> ${originDestination[0]} <span class="separator"></span> ${originDestination[1]}</div>`;
  }
  return '';
};
